/**
 * The map itself: a MapLibre canvas driven by `buildLayers`, with the layer panel, the feature
 * popup and the photo viewer around it.
 *
 * This component orchestrates; the style maths lives in `layers.ts`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FeatureCollection, Geometry, Point, Position } from 'geojson';
import { addProtocol, Map as MapLibreMap, NavigationControl, ScaleControl, type MapMouseEvent, type SourceSpecification, type StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box } from '@mantine/core';
import { MAP } from '@/theme/colors';
import { brand, shadows } from '@/theme/theme';
import { FeaturePopup, type FeatureTarget } from './FeaturePopup';
import { featureTitle, type FeatureTitle } from './featureTitle';
import { ChmReadout, type ChmReading } from './ChmReadout';
import { createChmSampler, heightOf } from './chmSampler';
import { LayerPanel, type BackdropChoice } from './LayerPanel';
import { isTreeLayer, layerKey } from './layerCatalog';
import type { LinkSource } from './linkSource';
import { BASEMAP_SOURCE, buildLayers, genusOf, TREE_FIELDS, GLYPHS_URL, interactiveLayerIds, objectIdOfLayer, pickHit, sourceId, type BuiltStyle, type LayerOptions, type LayerRef, type PhotoObjectRef, isCrownKind, type ObjectKind, isVisible, chmRampRange, MLS_INVALID, rasterTileZoom, SELECTED_STATE, UNIT_LEVEL } from './layers';
import { representativePoint } from './geometry';
import { PhotoLayer } from './PhotoLayer';
import { PMTILES_PROTOCOL, servePmtilesTile } from './pmtiles';
import { PopupFrame } from './PopupFrame';
import { loadPositioning, POSITIONING_FILE, withPositionSources } from './photoPositioning';
import { loadPhotoPoints, PhotoSourceError, type PhotoProperties } from './photoSource';
import { serveCachedTile, TILE_CACHE_PROTOCOL, tileCacheRequest } from './tileCache';
import type { TokenSource } from './tokenSource';
import { unionBounds, useRasterMeta } from './useRasterMeta';
import { loadLinkedCrownLayer, loadLinkedGpkgLayer, serveGpkgTile, VectorSourceError, type VectorProgress } from './vectorSource';

// Crown tiles come from the layer's own worker (crownLayer.ts), through MapLibre's protocol hook.
addProtocol('gpkg', async ({ url }) => ({ data: (await serveGpkgTile(url)).buffer as ArrayBuffer }));
// Archives of pre-cut tiles in the bucket, read by range and masked like the crown tiles (pmtiles.ts).
addProtocol(PMTILES_PROTOCOL, async ({ url }, abort) => ({ data: (await servePmtilesTile(url, abort.signal)).buffer as ArrayBuffer }));
// Tiler rasters through the Cache Storage, keyed without the token so a renewal misses nothing (tileCache.ts).
addProtocol(TILE_CACHE_PROTOCOL, serveCachedTile);

export interface FeatureClick {
  objectId: string;
  featureId: string;
  properties: Record<string, unknown>;
  /** Where the click landed, in WGS84. */
  lngLat?: { lng: number; lat: number };
}

/** The counterpart of a clicked feature in another layer (an MLS crown's stem, a stem's crown) and the line joining them. */
export interface RelatedFeature {
  /** The layer's object id, as its ref names it. */
  object: string;
  /** The feature's id in that layer's source. */
  id: string | number;
  /** The MapLibre source and source layer holding it, when not the object's own GeoJSON source (a tile archive). */
  source?: string;
  sourceLayer?: string;
  /**
   * From the clicked feature to its counterpart, in WGS84. Without it the map draws the line
   * itself, between the clicked feature's geometry and the counterpart's as its tiles hold it.
   */
  line?: [[number, number], [number, number]];
}

/**
 * The line from a clicked feature to its counterpart, found in the tiles the map holds: its
 * representative point and the counterpart's. Null when either is not loaded (out of view).
 */
function lineBetween(map: MapLibreMap, rel: RelatedFeature, clicked: Geometry | null): [[number, number], [number, number]] | null {
  const from = representativePoint(clicked);
  const found = map.querySourceFeatures(rel.source ?? sourceId(rel.object), { ...(rel.sourceLayer ? { sourceLayer: rel.sourceLayer } : {}), filter: ['==', ['id'], rel.id] });
  const to = found.length ? representativePoint(found[0]!.geometry as Geometry) : null;
  return from && to ? [from, to] : null;
}

/** The overlay that joins a clicked feature to its counterpart. */
export const LINK_SOURCE = 'src-link';
export const LINK_LAYER = 'lyr-link';
const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };

export interface MapViewProps {
  /** The trees report adds maturity and height layers to each crown; the panel offers their controls. */
  treeExtras?: boolean;
  refs: LayerRef[];
  options: LayerOptions;
  onOptionsChange: (options: LayerOptions) => void;
  tokenSource: TokenSource;
  onFeatureClick?: (click: FeatureClick) => void;
  className?: string;
  /** Acquisitions whose photo points the map draws from the bucket. */
  photos?: PhotoObjectRef[];
  /** Signs the bucket links; required whenever a `via: 'link'` ref or a photo acquisition is present. */
  linkSource?: LinkSource;
  onPhotoError?: (error: PhotoSourceError) => void;
  /** An acquisition's photo points, once read; the photo report computes its figures from them. */
  onPhotosLoaded?: (object: string, fc: FeatureCollection<Point, PhotoProperties>) => void;
  /** A `link` layer's GeoPackage could not be drawn; the rest of the map still is. */
  onVectorError?: (object: string, error: VectorSourceError) => void;
  onVectorLoaded?: (object: string, fc: FeatureCollection) => void;
  /** Progress of a `link` layer's load; `null` once it is drawn or has failed. */
  onVectorProgress?: (object: string, progress: VectorProgress | null) => void;
  /** GeoJSON the caller already read, by object id; drawn instead of loading those layers. */
  vectors?: Record<string, FeatureCollection>;
  /** The caller loads every `link` layer itself (true), or those of the listed objects, and passes them in `vectors`. */
  externalVectors?: boolean | string[];
  /** A clicked feature's attributes in a popup; off when the caller shows the click itself. */
  featurePopup?: boolean;
  /** A box to bring into view, as [west, south, east, north]; a new object moves the map again. */
  focus?: [number, number, number, number] | null;
  /** While a feature's popup is open, its counterpart is highlighted too and a line joins them. */
  relate?: (hit: { objectId: string; properties: Record<string, unknown> }) => RelatedFeature | null;
  /** A popup of the caller's own for a clicked feature, in place of the attribute list; null keeps the list. */
  renderPopup?: (target: FeatureTarget, close: () => void, title: FeatureTitle) => React.ReactNode | null;
  /** The page's switch of what lies under its layers, in the layer panel; the page swaps the raster in `refs`. */
  backdrop?: BackdropChoice;
}

/** The caller's popup for the clicked feature where it has one, placed as the attribute list is; the list otherwise. */
function Popup({ target, close, render, title }: { target: FeatureTarget; close: () => void; render?: MapViewProps['renderPopup']; title: FeatureTitle }) {
  const own = render?.(target, close, title);
  return (
    <PopupFrame point={target.point} testId={own ? 'custom-popup' : undefined}>
      {own ?? <FeaturePopup target={target} onClose={close} title={title} />}
    </PopupFrame>
  );
}

/** Below this width a map opens with its layer panel folded away. */
const NARROW_MAP_PX = 600;

/** Roughly the Czech Republic, until the raster bounds arrive. */
const HOME: [number, number] = [15.47, 49.82];
const HOME_ZOOM = 6;
const FIT_PADDING = 48;

/** The positions that bound a geometry: every vertex but those of a polygon's holes. */
function outerPositions(geometry: Geometry | null): Position[] {
  if (!geometry) return [];
  switch (geometry.type) {
    case 'Point':
      return [geometry.coordinates];
    case 'MultiPoint':
    case 'LineString':
      return geometry.coordinates;
    case 'MultiLineString':
      return geometry.coordinates.flat();
    case 'Polygon':
      return geometry.coordinates[0] ?? [];
    case 'MultiPolygon':
      return geometry.coordinates.flatMap((p) => p[0] ?? []);
    case 'GeometryCollection':
      return geometry.geometries.flatMap(outerPositions);
  }
}

/**
 * The smallest box holding every vector feature, used when no raster has bounds yet. A single
 * polygon must frame as itself: one vertex per feature put the map on a corner of an MLS plot.
 */
function vectorBounds(data: Record<string, FeatureCollection>): [number, number, number, number] | null {
  let box: [number, number, number, number] | null = null;
  for (const fc of Object.values(data)) {
    for (const feature of fc.features) {
      for (const [lon, lat] of outerPositions(feature.geometry)) {
        if (lon === undefined || lat === undefined) continue;
        box = box ? [Math.min(box[0], lon), Math.min(box[1], lat), Math.max(box[2], lon), Math.max(box[3], lat)] : [lon, lat, lon, lat];
      }
    }
  }
  return box;
}

/** The genera the tree layers' GeoJSON holds, sorted. */
function generaOf(refs: LayerRef[], data: Record<string, FeatureCollection>): string[] {
  const genera = new Set<string>();
  for (const ref of refs) {
    if (!isTreeLayer(ref)) continue;
    for (const feature of data[ref.object]?.features ?? []) addGenus(genera, feature.properties?.[TREE_FIELDS.genus]);
  }
  return [...genera].sort();
}

function addGenus(genera: Set<string>, raw: unknown): void {
  // An MLS segment that is no tree is not a genus to filter by.
  const genus = raw === MLS_INVALID ? '' : genusOf(raw);
  if (genus) genera.add(genus);
}

/** The unit levels the drawn units hold (their `level`). */
function unitLevelsOf(refs: LayerRef[], data: Record<string, FeatureCollection>): string[] {
  const levels = new Set<string>();
  for (const ref of refs) {
    const key = layerKey(ref);
    if (key !== 'units' && key !== 'unitsHit' && key !== 'unitsOutline') continue;
    for (const feature of data[ref.object]?.features ?? []) {
      const level = feature.properties?.[UNIT_LEVEL];
      if (typeof level === 'string' && level) levels.add(level);
    }
  }
  return [...levels].sort();
}

/** A hit worth a popup carries something besides its GeoPackage row id. */
const hasProperties = (properties: Record<string, unknown>) => Object.keys(properties).some((k) => k !== 'fid');

/**
 * Brings the map to `next`. Adding and removing is cheap and always right, so a changed source
 * or a changed layer order rebuilds; only paint, layout and filters are patched in place.
 */
/** Whether a source changed only in its tile URLs (a crown version bump), so new URLs refetch it in place. */
function tilesOnly(a: unknown, b: unknown): string[] | null {
  const x = a as { tiles?: string[] };
  const y = b as { tiles?: string[] };
  if (!y.tiles || JSON.stringify({ ...x, tiles: [] }) !== JSON.stringify({ ...y, tiles: [] })) return null;
  return y.tiles;
}

/** What a layer is apart from its paint, layout and filter: a change here means re-adding it. */
const layerFrame = (l: BuiltStyle['layers'][number]) => JSON.stringify([l.type, l.source, l['source-layer'], l.minzoom, l.maxzoom]);

/**
 * Brings the map to `next` by the smallest change: only the sources that changed are replaced
 * (one whose tile URLs alone changed is handed its new URLs), only new layers are added, kept
 * ones are moved into place and patched. Rebuilding everything on every change refetched every
 * raster tile and handed every GeoJSON source its data again. Returns the sources added afresh,
 * which are empty until their data is set again.
 */
function applyStyle(map: MapLibreMap, prev: BuiltStyle | null, next: BuiltStyle): Set<string> {
  const before = prev ?? { sources: {}, layers: [] };
  const replaced = new Set<string>();
  const retiled: [string, string[]][] = [];
  for (const [id, source] of Object.entries(next.sources)) {
    const old = before.sources[id];
    if (!old || JSON.stringify(old) === JSON.stringify(source)) continue;
    const tiles = tilesOnly(old, source);
    if (tiles) retiled.push([id, tiles]);
    else replaced.add(id);
  }
  const nextLayers = new Map(next.layers.map((l) => [l.id, l]));
  const kept = new Map<string, BuiltStyle['layers'][number]>();
  for (const layer of before.layers) {
    const wanted = nextLayers.get(layer.id);
    if (wanted && !replaced.has(layer.source) && layer.source in next.sources && layerFrame(wanted) === layerFrame(layer)) kept.set(layer.id, layer);
    else if (map.getLayer(layer.id)) map.removeLayer(layer.id);
  }
  for (const id of Object.keys(before.sources)) if ((!(id in next.sources) || replaced.has(id)) && map.getSource(id)) map.removeSource(id);
  const added = new Set<string>();
  for (const [id, source] of Object.entries(next.sources)) {
    if (id in before.sources && !replaced.has(id)) continue;
    map.addSource(id, source as unknown as SourceSpecification);
    added.add(id);
  }
  for (const [id, tiles] of retiled) (map.getSource(id) as { setTiles?: (tiles: string[]) => void } | undefined)?.setTiles?.(tiles);

  // MapLibre types these setters per property name; the diff only knows strings.
  const writer = map as unknown as {
    setPaintProperty(id: string, key: string, value: unknown): void;
    setLayoutProperty(id: string, key: string, value: unknown): void;
    setFilter(id: string, filter: unknown): void;
    moveLayer(id: string, beforeId?: string): void;
    addLayer(layer: unknown, beforeId?: string): void;
  };
  const sameOrder = before.layers.filter((l) => kept.has(l.id)).map((l) => l.id).join('|') === next.layers.filter((l) => kept.has(l.id)).map((l) => l.id).join('|');
  // Top down, each layer placed under the one above it.
  for (let i = next.layers.length - 1; i >= 0; i--) {
    const layer = next.layers[i]!;
    const above = next.layers[i + 1]?.id;
    const old = kept.get(layer.id);
    if (!old) {
      writer.addLayer(layer, above);
      continue;
    }
    if (!sameOrder) writer.moveLayer(layer.id, above);
    for (const [key, value] of Object.entries(layer.paint ?? {})) {
      if (JSON.stringify(old.paint?.[key]) !== JSON.stringify(value)) writer.setPaintProperty(layer.id, key, value);
    }
    for (const [key, value] of Object.entries(layer.layout ?? {})) {
      if (JSON.stringify(old.layout?.[key]) !== JSON.stringify(value)) writer.setLayoutProperty(layer.id, key, value);
    }
    if (JSON.stringify(old.filter) !== JSON.stringify(layer.filter)) writer.setFilter(layer.id, layer.filter);
  }
  return added;
}

/** §7: white controls with the brand border and forest icons. MapLibre draws them outside React. */
const CONTROL_CSS = `
.maplibregl-ctrl-group {
  background: ${MAP.controlBg};
  border: 1px solid ${MAP.controlBorder};
  border-radius: 10px;
  box-shadow: ${shadows.card};
}
.maplibregl-ctrl-group button + button { border-top: 1px solid ${MAP.controlBorder}; }
.maplibregl-ctrl-group button .maplibregl-ctrl-icon { filter: none; }
.maplibregl-ctrl-scale {
  background: ${MAP.controlBg};
  border-color: ${MAP.controlBorder};
  color: ${brand.textMuted};
  border-radius: 4px;
}
.maplibregl-ctrl-attrib { background: ${MAP.attributionBg}; font-size: 11px; }
`;

export function MapView({ refs, options, onOptionsChange, tokenSource, onFeatureClick, className, photos, linkSource, onPhotoError, onPhotosLoaded, onVectorError, onVectorLoaded, onVectorProgress, vectors, externalVectors, treeExtras, featurePopup = true, focus, relate, renderPopup, backdrop }: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<MapLibreMap | null>(null);
  const [ready, setReady] = useState(false);
  const [target, setTarget] = useState<FeatureTarget | null>(null);
  const [chmReading, setChmReading] = useState<ChmReading | null>(null);
  // Measured once: whether the panel starts folded away when the reader has not chosen yet.
  const [narrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < NARROW_MAP_PX);

  const { metas, errors: metaErrors } = useRasterMeta(refs, tokenSource);
  const metasRef = useRef(metas);
  const refsRef = useRef(refs);
  const sampler = useMemo(() => createChmSampler(tokenSource), [tokenSource]);
  const warnings = Object.fromEntries(Object.entries(metas).map(([id, m]) => [id, m.warnings]));
  const valueRanges = Object.fromEntries(Object.entries(metas).flatMap(([id, m]) => (m.value_range?.[0] ? [[id, m.value_range[0]]] : [])));

  const linked = !!relate;
  const built = useMemo(() => {
    const style = buildLayers(refs, options, photos ?? []);
    if (!linked) return style;
    // Always there, empty until a click: setting its data never rebuilds the style.
    style.sources[LINK_SOURCE] = { type: 'geojson', data: { type: 'FeatureCollection', features: [] } };
    style.layers.push({ id: LINK_LAYER, type: 'line', source: LINK_SOURCE, paint: { 'line-color': MAP.linkLine, 'line-width': 2, 'line-dasharray': [2, 1] } });
    return style;
  }, [refs, options, photos, linked]);
  const builtRef = useRef(built);
  const appliedRef = useRef<BuiltStyle | null>(null);
  /** The GeoJSON each source was last given, until the style is rebuilt. */
  const dataRef = useRef(new Map<string, FeatureCollection>());
  const fittedRef = useRef(false);
  const clickRef = useRef(onFeatureClick);
  const optionsRef = useRef(options);
  const onPhotoErrorRef = useRef(onPhotoError);
  const onPhotosLoadedRef = useRef(onPhotosLoaded);
  const onVectorErrorRef = useRef(onVectorError);
  const onVectorLoadedRef = useRef(onVectorLoaded);
  const onVectorProgressRef = useRef(onVectorProgress);

  // The GeoJSON of each link layer and acquisition is fetched once per mount and handed to its
  // source; a style rebuild re-adds the source empty, so the data is re-applied whenever it comes back.
  const [vectorData, setVectorData] = useState<Record<string, FeatureCollection>>({});
  const drawn = useMemo(() => ({ ...vectorData, ...(vectors ?? {}) }), [vectorData, vectors]);

  // The map's own handlers outlive a render, so they read the latest style and callback here.
  useEffect(() => {
    builtRef.current = built;
    clickRef.current = onFeatureClick;
    optionsRef.current = options;
    metasRef.current = metas;
    refsRef.current = refs;
    onPhotoErrorRef.current = onPhotoError;
    onPhotosLoadedRef.current = onPhotosLoaded;
    onVectorErrorRef.current = onVectorError;
    onVectorLoadedRef.current = onVectorLoaded;
    onVectorProgressRef.current = onVectorProgress;
  }, [built, onFeatureClick, options, onPhotoError, onPhotosLoaded, onVectorError, onVectorLoaded, onVectorProgress, metas, refs]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const style: StyleSpecification = { version: 8, sources: {}, layers: [], glyphs: GLYPHS_URL };
    const instance = new MapLibreMap({
      container,
      style,
      center: HOME,
      zoom: HOME_ZOOM,
      attributionControl: { compact: true },
      transformRequest: tileCacheRequest(tokenSource),
      // The print path reads the WebGL canvas, which is cleared after every paint otherwise (S6).
      // MapLibre 6 takes the context attributes rather than a top-level flag.
      canvasContextAttributes: { preserveDrawingBuffer: true },
    });
    instance.addControl(new NavigationControl({ showCompass: false }), 'top-left');
    instance.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left');
    instance.on('load', () => setReady(true));
    instance.on('click', (e: MapMouseEvent) => {
      const ids = interactiveLayerIds(builtRef.current).filter((id) => instance.getLayer(id));
      // A tree wins over the area it lies in, however the two are stacked (pickHit).
      const hit = ids.length ? pickHit(builtRef.current, instance.queryRenderedFeatures(e.point, { layers: ids })) : undefined;
      if (!hit) {
        setTarget(null);
        return;
      }
      const properties = (hit.properties ?? {}) as Record<string, unknown>;
      if (!hasProperties(properties)) {
        setTarget(null);
        return;
      }
      const objectId = objectIdOfLayer(hit.layer.id);
      clickRef.current?.({ objectId, featureId: String(properties.id ?? hit.id ?? ''), properties, lngLat: e.lngLat ? { lng: e.lngLat.lng, lat: e.lngLat.lat } : undefined });
      const feature = hit.id === undefined ? undefined : { source: hit.source, sourceLayer: hit.sourceLayer, id: hit.id };
      setTarget({ objectId, properties, point: { x: e.point.x, y: e.point.y }, feature, geometry: (hit.geometry as Geometry | undefined) ?? null });
    });
    // The canopy height under the cursor: the top-most visible CHM, read from its tile. One
    // read at a time; a move during a read is served after it from the latest position.
    let latest: { lng: number; lat: number } | null = null;
    let reading = false;
    let serial = 0;
    const readChm = async () => {
      if (reading || !latest) return;
      const at = latest;
      latest = null;
      const options = optionsRef.current;
      const chm = refsRef.current.find((r) => r.via === 'tiler' && r.object_kind === 'chm' && isVisible(options, r.object, r.object_kind));
      const range = chm && metasRef.current[chm.object]?.value_range[0];
      if (!chm || !range) {
        setChmReading(null);
        return;
      }
      reading = true;
      const mine = ++serial;
      try {
        // The zoom of the tiles on screen, capped at the raster sources' maxzoom (MapLibre's 22).
        const z = Math.min(22, rasterTileZoom(instance.getZoom()));
        const t = await sampler.sample(chm, options.baseUrl, z, at.lng, at.lat);
        if (mine === serial) setChmReading(t === null ? null : heightOf(t, chmRampRange(range)));
      } finally {
        reading = false;
        if (latest) void readChm();
      }
    };
    instance.on('mousemove', (e: MapMouseEvent) => {
      latest = { lng: e.lngLat.lng, lat: e.lngLat.lat };
      void readChm();
    });
    instance.on('mouseout', () => {
      latest = null;
      serial++;
      setChmReading(null);
    });
    setMap(instance);
    appliedRef.current = null;
    return () => {
      instance.remove();
      setMap(null);
      setReady(false);
    };
  }, [tokenSource, sampler]);

  useEffect(() => {
    if (!map || !ready) return;
    // A source added afresh is empty: its data is handed over again.
    for (const id of applyStyle(map, appliedRef.current, built)) dataRef.current.delete(id.replace(/^src-/, ''));
    appliedRef.current = built;
  }, [map, ready, built]);

  // A refreshed token changes no tile URL — the token is added by `transformRequest` — so the
  // tiles that failed while it was dead would never be asked for again. Handing each tiler
  // source its own tile URLs back makes MapLibre drop its cache and refetch them (V4); the tiles
  // that did load come back from the tile cache (tileCache.ts), which ignores the token.
  useEffect(() => {
    if (!map || !ready) return;
    return tokenSource.onRefresh(() => {
      for (const [id, source] of Object.entries(builtRef.current.sources)) {
        if (id === BASEMAP_SOURCE || source.type === 'vector') continue; // crown tiles carry no token
        const tiles = (source as { tiles?: string[] }).tiles;
        const live = map.getSource(id) as { setTiles?: (tiles: string[]) => void } | undefined;
        if (tiles && live?.setTiles) live.setTiles(tiles);
      }
    });
  }, [map, ready, tokenSource]);

  // The feature whose popup is open is drawn highlighted (layers.ts reads the state).
  const selected = featurePopup ? target?.feature : undefined;
  useEffect(() => {
    if (!map || !selected) return;
    map.setFeatureState(selected, { [SELECTED_STATE]: true });
    return () => {
      // The source may be gone already: a changed style rebuilds it.
      if (map.getSource(selected.source)) map.removeFeatureState(selected, SELECTED_STATE);
    };
  }, [map, selected]);

  const related = useMemo(() => (featurePopup && target && relate ? relate({ objectId: target.objectId, properties: target.properties }) : null), [featurePopup, target, relate]);
  const relatedKey = related ? JSON.stringify(related) : '';
  useEffect(() => {
    if (!map || !ready || !relatedKey) return;
    const { object, id, source, sourceLayer } = JSON.parse(relatedKey) as RelatedFeature;
    const feature = { source: source ?? sourceId(object), ...(sourceLayer ? { sourceLayer } : {}), id };
    if (map.getSource(feature.source)) map.setFeatureState(feature, { [SELECTED_STATE]: true });
    return () => {
      if (map.getSource(feature.source)) map.removeFeatureState(feature, SELECTED_STATE);
    };
  }, [map, ready, relatedKey]);
  useEffect(() => {
    if (!map || !ready || !linked) return;
    const rel = relatedKey ? (JSON.parse(relatedKey) as RelatedFeature) : null;
    const ends = rel ? (rel.line ?? lineBetween(map, rel, target?.geometry ?? null)) : null;
    const line: FeatureCollection = ends ? { type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: ends } }] } : EMPTY;
    (map.getSource(LINK_SOURCE) as { setData?: (data: unknown) => void } | undefined)?.setData?.(line);
    // The target's geometry is read with the key; the key alone decides when to redraw.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, ready, linked, relatedKey, built]);

  // Print gives the canvas a different box, and back again afterwards; MapLibre sizes its
  // drawing buffer itself and has to be told (S6).
  useEffect(() => {
    if (!map) return;
    const resize = () => map.resize();
    window.addEventListener('beforeprint', resize);
    window.addEventListener('afterprint', resize);
    return () => {
      window.removeEventListener('beforeprint', resize);
      window.removeEventListener('afterprint', resize);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !ready || fittedRef.current) return;
    const bounds = unionBounds(Object.values(metas)) ?? vectorBounds(drawn);
    if (!bounds) return;
    fittedRef.current = true;
    map.fitBounds(
      [
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ],
      { padding: FIT_PADDING, duration: 0 },
    );
  }, [map, ready, metas, drawn]);

  useEffect(() => {
    if (!map || !ready || !focus) return;
    fittedRef.current = true;
    map.fitBounds(
      [
        [focus[0], focus[1]],
        [focus[2], focus[3]],
      ],
      { padding: FIT_PADDING, duration: 0 },
    );
  }, [map, ready, focus]);

  // Which genera the tree data actually holds, so the genus filter offers only what is on the
  // map: from the GeoJSON the map holds, and from the tiles of an archive as they arrive (only
  // those loaded so far are known; the list grows as the reader moves).
  const dataGenera = useMemo(() => generaOf(refs, drawn), [refs, drawn]);
  const [tileGenera, setTileGenera] = useState<string[]>([]);
  const tiledKey = JSON.stringify(refs.filter((r) => r.tiles && isTreeLayer(r)).map((r) => [sourceId(r.tiles!.source), r.tiles!.layers.points, r.tiles!.layers.polygons]));
  useEffect(() => {
    if (!map || !ready) return;
    const tiled = JSON.parse(tiledKey) as [string, string, string][];
    if (!tiled.length) return;
    const sources = new Set(tiled.map(([source]) => source));
    let dirty = true;
    const onData = (e: { sourceId?: string }) => {
      if (e.sourceId && sources.has(e.sourceId)) dirty = true;
    };
    const collect = () => {
      if (!dirty) return;
      dirty = false;
      const found = new Set<string>();
      for (const [source, points, polygons] of tiled) {
        if (!map.getSource(source)) continue;
        for (const sourceLayer of [points, polygons]) for (const f of map.querySourceFeatures(source, { sourceLayer })) addGenus(found, f.properties?.[TREE_FIELDS.genus]);
      }
      setTileGenera((prev) => {
        const next = [...new Set([...prev, ...found])].sort();
        return next.join('|') === prev.join('|') ? prev : next;
      });
    };
    map.on('sourcedata', onData);
    map.on('idle', collect);
    return () => {
      map.off('sourcedata', onData);
      map.off('idle', collect);
    };
  }, [map, ready, tiledKey]);
  const genera = useMemo(() => [...new Set([...dataGenera, ...tileGenera])].sort(), [dataGenera, tileGenera]);
  const unitLevels = useMemo(() => unitLevelsOf(refs, drawn), [refs, drawn]);

  // Keyed by content: callers filter `refs` on every render, and a fresh array must not refetch.
  const external = (object: string) => externalVectors === true || (Array.isArray(externalVectors) && externalVectors.includes(object));
  const linkKey = JSON.stringify(refs.filter((r) => r.via === 'link' && !external(r.object)).map((r) => [r.object, r.path, r.digest, r.object_kind]));
  useEffect(() => {
    if (!linkSource) return;
    let alive = true;
    for (const [object, path, digest, kind] of JSON.parse(linkKey) as [string, string, string, ObjectKind][]) {
      const progress = { onProgress: (p: VectorProgress) => alive && onVectorProgressRef.current?.(object, p) };
      const loading = isCrownKind(kind) ? loadLinkedCrownLayer({ object, path, digest }, linkSource, progress).then((c) => c.points) : loadLinkedGpkgLayer({ object, path, digest }, linkSource, progress);
      loading
        .then((fc) => {
          if (!alive) return;
          setVectorData((prev) => ({ ...prev, [object]: fc }));
          onVectorLoadedRef.current?.(object, fc);
        })
        .catch((e: unknown) => {
          if (alive) onVectorErrorRef.current?.(object, e instanceof VectorSourceError ? e : new VectorSourceError('fetch', String(e)));
        })
        .finally(() => alive && onVectorProgressRef.current?.(object, null));
    }
    return () => {
      alive = false;
    };
  }, [linkSource, linkKey]);

  useEffect(() => {
    if (!linkSource || !photos?.length) return;
    let alive = true;
    for (const ref of photos) {
      const positioning = ref.positioning;
      // The per photo table only when the run wrote it; without it (or when it cannot be read) the
      // sources still come from the photo points and the run's summary, without most accuracies.
      const positions = positioning?.positions_file
        ? linkSource
            .url(ref.object, POSITIONING_FILE)
            .then((url) => loadPositioning(url))
            .catch(() => null)
        : Promise.resolve(null);
      linkSource
        .url(ref.object, 'photos.gpkg')
        .then((url) => loadPhotoPoints(url))
        .then(async (points) => (positioning ? withPositionSources(points, positioning, await positions) : points))
        .then((fc) => {
          if (!alive) return;
          setVectorData((prev) => ({ ...prev, [ref.object]: fc }));
          onPhotosLoadedRef.current?.(ref.object, fc);
        })
        .catch((e: unknown) => {
          if (alive) onPhotoErrorRef.current?.(e instanceof PhotoSourceError ? e : new PhotoSourceError('fetch', String(e)));
        });
    }
    return () => {
      alive = false;
    };
  }, [linkSource, photos]);

  useEffect(() => {
    if (!map || !ready) return;
    for (const [object, fc] of Object.entries(drawn)) {
      // A crown source is tiles: its points are here only for the genera and the bounds.
      if (built.sources[sourceId(object)]?.type !== 'geojson') continue;
      // Handing MapLibre the same data again costs as much as new data: only a change is passed on.
      if (dataRef.current.get(object) === fc) continue;
      const live = map.getSource(sourceId(object)) as { setData?: (data: unknown) => void } | undefined;
      live?.setData?.(fc);
      dataRef.current.set(object, fc);
    }
  }, [map, ready, built, drawn]);

  // What was clicked, from the layer it was hit in.
  const titleOf = (t: FeatureTarget) => {
    const ref = refs.find((r) => r.object === t.objectId);
    return featureTitle(ref?.object_kind, ref?.table, t.properties);
  };

  return (
    <Box className={className} pos="relative" w="100%" h="100%" style={{ overflow: 'hidden' }}>
      <style>{CONTROL_CSS}</style>
      <div ref={containerRef} style={{ position: 'absolute', inset: 0, backgroundColor: MAP.background }} />

      <Box pos="absolute" top={12} right={12} style={{ zIndex: 2 }}>
        <LayerPanel refs={refs} options={options} onOptionsChange={onOptionsChange} photos={photos} warnings={warnings} metaErrors={metaErrors} valueRanges={valueRanges} treeExtras={treeExtras} narrow={narrow} genera={genera} unitLevels={unitLevels} backdrop={backdrop} />
      </Box>

      {featurePopup && target && <Popup target={target} close={() => setTarget(null)} render={renderPopup} title={titleOf(target)} />}
      {chmReading && <ChmReadout reading={chmReading} />}

      {map &&
        ready &&
        linkSource &&
        (photos ?? []).map((ref) => <PhotoLayer key={ref.object} map={map} object={ref.object} linkSource={linkSource} />)}
    </Box>
  );
}
